import React, { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { SxProps } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';

import Button from '@ts-core/components/Button';

import FeedbackForm from './FeedbackForm';
import ScoreForm from './ScoreForm';

interface ActivityAnswerFeedbackFormProps {
  sx?: SxProps;

  mode: 'score' | 'feedback' | 'full';

  onGradeAnswer: (file?: File, text?: string, score?: string) => void;
  gradeLoading?: boolean;
  gradeError?: string;

  onCreateFeedback: (file?: File, text?: string) => void;
  createFeedbackLoading?: boolean;
  createFeedbackError?: string;

  onCreateScore: (score: string) => void;
  createScoreLoading?: boolean;
  createScoreError?: string;
}

const ActivityAnswerFeedbackForm = (props: ActivityAnswerFeedbackFormProps) => {
  const {
    sx,
    onGradeAnswer,
    gradeLoading,
    gradeError,
    onCreateFeedback,
    createFeedbackLoading,
    createFeedbackError,
    onCreateScore,
    createScoreLoading,
    createScoreError,
    mode,
  } = props;
  const intl = useIntl();

  const [file, setSelectedFile] = useState<File>();
  const [text, setText] = useState<string>('');
  const [score, setScore] = useState<string>('');
  const [clientError, setClientError] = useState<string>();

  const error = clientError || gradeError || createFeedbackError || createScoreError;

  const handleGrade = useCallback(() => {
    if (!file && (!text || text.length === 0) && (!score || score.length === 0)) {
      setClientError('Debes seleccionar un archivo, escribir una devolución o puntuar la actividad.');
    }
    onGradeAnswer(file, text, score);
  }, [text, file, score, onGradeAnswer]);

  const handleCreateFeedback = useCallback(() => {
    if (!file && (!text || text.length === 0)) {
      setClientError('Debes seleccionar un archivo, escribir una devolución.');
    }
    onCreateFeedback(file, text);
  }, [text, file, onCreateFeedback]);

  const handleCreateScore = useCallback(() => {
    if (!score || score.length === 0) {
      setClientError('Debes puntuar la actividad.');
    }
    onCreateScore(score);
  }, [score, onCreateScore]);

  return (
    <Box width="100%" sx={sx}>
      <Divider sx={{ my: 2 }} />
      <Stack mb={2} spacing={2}>
        {(mode === 'score' || mode === 'full') && (
          <ScoreForm mode={mode} onScoreChange={setScore} score={score} onSubmit={handleCreateScore} isSubmitting={createScoreLoading} />
        )}
        {(mode === 'feedback' || mode === 'full') && <FeedbackForm onSelectFile={setSelectedFile} file={file} text={text} onTextChange={setText} />}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
      </Stack>
      {(mode === 'feedback' || mode === 'full') && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            onClick={mode === 'feedback' ? handleCreateFeedback : handleGrade}
            loading={createFeedbackLoading || gradeLoading}
          >
            {intl.formatMessage({ id: 'button.save.title' })}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ActivityAnswerFeedbackForm;
